import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PromptTips } from './PromptTips';
import { 
  INDUSTRIES, 
  TONES, 
  COMPLEXITY_LEVELS, 
  OUTPUT_FORMATS,
  PromptData 
} from '../types';

export function WelcomeScreen() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PromptData>({
    topic: '',
    goal: '',
    industry: INDUSTRIES[0],
    audience: '',
    tone: TONES[0],
    complexity: COMPLEXITY_LEVELS[0],
    format: OUTPUT_FORMATS[0]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.topic || !formData.goal || !formData.industry || !formData.audience) {
      alert('Please fill in all required fields');
      return;
    }
    navigate('/result', { state: { promptData: formData } });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-8">
        {/* Icon and Title Section */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-50 p-4 rounded-full">
              <span className="text-4xl">✨</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            AI Prompt Generator
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Create effective, well-structured prompts for AI systems like ChatGPT. 
            Simply enter your topic and goal, and we'll help you craft the perfect prompt.
          </p>
        </div>

        {/* Main Form Section */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-8 shadow-lg mb-8">
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                What topic or subject do you need help with?*
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                placeholder="E.g., JavaScript programming, digital marketing, creative writing"
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none placeholder-gray-400"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                What specific goal or task do you want to achieve?*
              </label>
              <input
                type="text"
                value={formData.goal}
                onChange={(e) => setFormData(prev => ({ ...prev, goal: e.target.value }))}
                placeholder="E.g., learn async/await, create a marketing strategy, write a compelling story"
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none placeholder-gray-400"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Who is your target audience?*
              </label>
              <input
                type="text"
                value={formData.audience}
                onChange={(e) => setFormData(prev => ({ ...prev, audience: e.target.value }))}
                placeholder="E.g., Marketing professionals, Students, etc."
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none placeholder-gray-400"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Select your industry*
              </label>
              <select
                value={formData.industry}
                onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                required
              >
                {INDUSTRIES.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Preferred tone of voice
              </label>
              <select
                value={formData.tone}
                onChange={(e) => setFormData(prev => ({ ...prev, tone: e.target.value }))}
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                {TONES.map(tone => (
                  <option key={tone} value={tone}>{tone}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Content complexity level
              </label>
              <select
                value={formData.complexity}
                onChange={(e) => setFormData(prev => ({ ...prev, complexity: e.target.value }))}
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                {COMPLEXITY_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Preferred output format
              </label>
              <select
                value={formData.format}
                onChange={(e) => setFormData(prev => ({ ...prev, format: e.target.value }))}
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                {OUTPUT_FORMATS.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="pt-6 text-center">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg shadow-md transition-transform transform hover:scale-105 focus:ring-4 focus:ring-blue-300 focus:outline-none"
            >
              Generate Prompt
            </button>
            <p className="text-sm text-gray-500 mt-4">* Required fields</p>
          </div>
        </form>

        {/* Tips Section - Moved below the form */}
        <PromptTips />
      </div>
    </div>
  );
}